import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
};

export const doctorAPI = {
  createProfile: (data) => api.post('/doctor/profile', data),
  getProfile: () => api.get('/doctor/profile'),
  uploadPhoto: (formData) => api.post('/doctor/profile/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getCases: () => api.get('/doctor/cases'),
  getCaseFiles: (caseId) => api.get(`/doctor/cases/${caseId}/files`),
  acceptCase: (caseId) => api.post(`/doctor/cases/${caseId}/accept`),
  declineCase: (caseId) => api.post(`/doctor/cases/${caseId}/decline`),
  previewReport: (data) => api.post('/doctor/report/preview', data, { responseType: 'blob' }),
  createReport: (data) => api.post('/doctor/report', data),
  getEarnings: () => api.get('/doctor/earnings'),
};

export const patientAPI = {
  getDoctors: () => api.get('/patient/doctors'),
  createCase: (data) => api.post('/patient/cases', data),
  getCases: () => api.get('/patient/cases'),
};

export const fileAPI = {
  upload: (caseId, file) => {
    const formData = new FormData();
    formData.append('case_id', caseId);
    formData.append('file', file);
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
};

export const paymentAPI = {
  confirm: (caseId) => api.post('/payment/confirm', null, { params: { case_id: caseId } }),
};

export const adminAPI = {
  getPendingDoctors: () => api.get('/admin/doctors/pending'),
  getAllDoctors: () => api.get('/admin/doctors/all'),
  approveDoctor: (userId) => api.post(`/admin/doctors/${userId}/approve`),
  rejectDoctor: (userId) => api.post(`/admin/doctors/${userId}/reject`),
  updateDoctorProfile: (userId, data) => api.put(`/admin/doctors/${userId}/profile`, null, { params: data }),
  uploadDoctorPhoto: (userId, formData) => api.post(`/admin/doctors/${userId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStats: () => api.get('/admin/stats'),
  getAllCases: () => api.get('/admin/cases'),
  
  // Case review (images)
  getCasesPendingReview: () => api.get('/admin/cases/pending-review'),
  approveCaseForDoctor: (caseId) => api.post(`/admin/cases/${caseId}/approve-for-doctor`),
  rejectCase: (caseId, reason) => api.post(`/admin/cases/${caseId}/reject`, null, { params: { reason } }),
  
  // Report review and translation
  getPendingReports: () => api.get('/admin/reports/pending'),
  approveTranslateSend: (reportId, language) => api.post(`/admin/reports/${reportId}/approve-translate-send`, { target_language: language }),
  
  // Blog management
  createBlogPost: (data) => api.post('/admin/blog', data),
  updateBlogPost: (postId, data) => api.put(`/admin/blog/${postId}`, data),
  deleteBlogPost: (postId) => api.delete(`/admin/blog/${postId}`),
  
  // CMS management
  getCMSSettings: () => api.get('/cms/settings'),
  updateCMSSettings: (data) => api.put('/admin/cms/settings', data),
  uploadLogo: (formData) => api.post('/admin/cms/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  
  // Admin user management
  changePassword: (currentPassword, newPassword) => api.post('/admin/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
  resetAllUsers: () => api.post('/admin/reset-all-users'),
  
  // Patient management
  getAllPatients: () => api.get('/admin/patients'),
  
  // User deletion management
  getDoctorsForDeletion: () => api.get('/admin/users/doctors'),
  getPatientsForDeletion: () => api.get('/admin/users/patients'),
  deleteSelectedUsers: (userIds) => api.post('/admin/users/delete', { user_ids: userIds }),
};

export const blogAPI = {
  getPosts: (publishedOnly = true) => api.get(`/blog?published_only=${publishedOnly}`),
  getPost: (postId) => api.get(`/blog/${postId}`),
};

export const notificationAPI = {
  getNotifications: () => api.get('/notifications'),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
};

export default api;
